import React, { useState, Fragment } from "react";
import { Link, withRouter, RouteComponentProps } from "react-router-dom";
import { UtilityFunctions } from "../../utils/UtilityFunctions";
import { STRING_CONSTANTS } from "../../utils/constants/stringConstants";
import { Helmet } from 'react-helmet';
import { userService, cookieService } from "../../_services";
import "./HRSimulatorSignup.css";

interface HRSimulatorSignupProps extends RouteComponentProps<any> {}

const HRSimulatorSignup: React.FC<HRSimulatorSignupProps> = (props) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [affiliateCode, setAffiliateCode] = useState(localStorage.getItem("referralCode") || '');
  const [pwdType, setPwdType] = useState("password");
  const [btnClick, setBtnClick] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [mobileNumber, setMobileNumber] = useState('');

  const hasNumber = /\d/;
  const isEmailValid = UtilityFunctions.validateEmail(email);
  const isPasswordValid = password.length >= 8 && hasNumber.test(password) && !UtilityFunctions.checkSpaceInPassword(password);
  const isMobileValid = !UtilityFunctions.isEmptyOrNullOrUndefined(mobileNumber);
  const isFormValid = isEmailValid && isPasswordValid && isMobileValid;

  const togglePassword = () => {
    setPwdType(pwdType === "password" ? "text" : "password");
  };

  const scrollToForm = () => {
    document.getElementById('hr-signup-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBtnClick(true);
    setApiError(null);

    if (!isFormValid) return;

    setIsLoading(true);
    try {
      const signupCode = affiliateCode.trim();
      const signupData: any = {
        email,
        password,
        mobileNumber,
        type: 'Mploi-er',
        is_hr_simulator_signup: true,
        isAccountsSignup: true,
        is_accounts_signup: true,
        signup_code: signupCode,
        signupCode,
        referred_by_code: signupCode,
        referredByCode: signupCode,
      };

      await userService.signup(signupData, signupCode, true);

      const loginResponse = await userService.login({ email, password });

      if (loginResponse.data && loginResponse.data.user && loginResponse.data.user.token) {
        localStorage.setItem('access_token', loginResponse.data.user.token.access);
        localStorage.setItem('refresh_token', loginResponse.data.user.token.refresh);
        localStorage.setItem('user_data', JSON.stringify(loginResponse.data.user));
        localStorage.setItem('isGuest', 'false');

        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.token, loginResponse.data.user.token.access);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, loginResponse.data.user);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.isGuest, false);

        await userService.getUserData();
        window.location.href = 'https://simulation.mploi.com/hr';
      }
    } catch (error: any) {
      console.error("Signup failed:", error);
      if (error && Array.isArray(error) && error[0] === 'Email') {
        setApiError("Email already exists");
      } else {
        setApiError(
          error.response?.data?.message ||
          error.message ||
          "An unexpected error occurred during signup."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const terms = { pathname: `/terms`, state: {} };
  const privacy = { pathname: `/privacy`, state: {} };

  return (
    <Fragment>
      <Helmet>
        <title>MPLOI AI HR Assistant - HR Expertise for Every Business</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Urbanist:wght@400;500;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="hr-signup">
        {/* Navigation */}
        <nav className="hr-nav">
          <img
            src={UtilityFunctions.getImageURL() + "logo_White.svg"}
            alt="MPLOI Logo"
            className="hr-nav-logo"
            onClick={() => props.history.push("/")}
          />
          <ul className="hr-nav-links">
            <li><a href="https://mploi.com/#about">About Us</a></li>
            <li><a href="https://mploi.com/#services">Services</a></li>
            <li><a href="https://mploi.com/blog">Blog</a></li>
            <li><a href="https://mploi.com/#pricing">Pricing</a></li>
            <li><a href="https://mploi.com/#contact">Contact</a></li>
          </ul>
          <div className="hr-nav-actions">
            <button className="hr-btn-login" onClick={() => props.history.push('/signin')}>Log In</button>
            <button className="hr-btn-get-started" onClick={scrollToForm}>Get Started</button>
          </div>
        </nav>

        {/* Hero */}
        <section className="hr-hero">
          <div className="hr-hero-content">
            <h1>AI HR<br/>Simulator</h1>
            <p className="hr-hero-tagline">One AI. Every HR question. Built for Australian businesses that need to get it right.</p>
            <p className="hr-hero-subtitle">
              Navigating Australian workplace law shouldn't require a dedicated HR department.
              MPLOI's AI HR Assistant delivers instant, expert HR guidance around the clock —
              empowering businesses of every size to handle workplace challenges with confidence, reduce compliance risk, and protect their people.
            </p>
            <button className="hr-hero-cta" onClick={scrollToForm}>
              Join NOW <span style={{fontSize: '20px'}}>↗</span>
            </button>
          </div>
        </section>

        {/* Content */}
        <section className="hr-content">
          <div className="hr-content-inner">
            <span className="hr-section-label">About HR Simulator</span>
            <h2>HR expertise shouldn't be a luxury — it should be standard.</h2>
            <div className="hr-content-body">
              <p>
                Every Australian business faces HR challenges — Fair Work compliance, Modern Award interpretation,
                workplace health and safety obligations, managing underperformance, and navigating the complexities
                of termination and redundancy. The cost of getting these wrong can be devastating: unfair dismissal
                claims, regulatory penalties, and damaged workplace culture.
              </p>
              <p>
                For small businesses without a dedicated HR team, even routine questions about leave entitlements,
                notice periods, or award classifications can spiral into expensive legal consultations. For larger
                organisations, HR teams are stretched thin — buried in repetitive queries that pull focus from
                strategic workforce planning and employee engagement.
              </p>
              <p>
                <strong>MPLOI's AI HR Assistant changes that equation entirely.</strong>
              </p>
              <p>
                What sets our solution apart is what's powering it. We've built an explainable AI model specifically
                trained on Australian workplace relations frameworks — the Fair Work Act 2009, Modern Awards, the
                Work Health and Safety Act, the Small Business Fair Dismissal Code, and the National Employment
                Standards. Our model draws continuously from authoritative sources and is constantly retrained to
                reflect the latest legislative changes, ensuring every response is grounded in current, locally
                relevant, and jurisdictionally accurate information.
              </p>
              <p>
                Importantly, the AI HR Assistant isn't designed to replace your HR specialist or employment lawyer —
                and we're upfront about that. What it does brilliantly is give you a trusted, knowledgeable sounding
                board so you understand exactly where you stand before taking the next step. That clarity alone can
                save businesses significant time, money, and costly missteps from the very outset.
              </p>
              <p>
                <strong>For smaller businesses, it's your virtual HR department</strong> — cutting through complex
                workplace jargon, answering pressing questions about Fair Work obligations instantly, and building
                a historical library of HR solutions tailored specifically to your business. Whether it's understanding
                your obligations during a performance management process, interpreting a Modern Award clause, or
                knowing your rights during a workplace investigation — the AI HR Assistant has you covered.
              </p>
              <p>
                <strong>For larger enterprises, it's a powerful force multiplier for your existing HR team.</strong> Junior
                HR professionals gain an exceptional training and reference resource, routine compliance queries are
                resolved instantly, and your senior HR leaders reclaim the valuable hours needed to focus on complex
                workforce strategy, enterprise bargaining, and organisational development. With on-premise deployment
                options available for industries with strict security requirements, the AI HR Assistant adapts to
                your environment — not the other way around.
              </p>
              <p className="hr-closing">
                One AI. Every HR question. Built for Australian businesses that need to get it right.
              </p>
            </div>
          </div>
        </section>

        {/* Signup Form */}
        <section className="hr-form-section" id="hr-signup-form">
          <div className="hr-form-container">
            <h2 className="hr-form-title">Create Your Account</h2>

            <form onSubmit={handleSubmit} noValidate>
              <div className="hr-form-group">
                <input
                  type="email"
                  placeholder="Email Address"
                  className={`hr-form-input ${btnClick && !isEmailValid ? 'error' : ''}`}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                {btnClick && !isEmailValid && <div className="hr-form-error">Please enter a valid email address</div>}
              </div>

              <div className="hr-form-group">
                <input
                  type="tel"
                  placeholder="Mobile Phone"
                  className={`hr-form-input ${btnClick && !isMobileValid ? 'error' : ''}`}
                  value={mobileNumber}
                  onChange={(e) => { setMobileNumber(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                {btnClick && !isMobileValid && <div className="hr-form-error">Please enter your mobile number</div>}
              </div>

              <div className="hr-form-group">
                <input
                  type={pwdType}
                  placeholder="Password"
                  className={`hr-form-input ${btnClick && !isPasswordValid ? 'error' : ''}`}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                <div className="hr-form-icon" onClick={togglePassword}>
                  <img
                    src={pwdType === "password" ?
                      UtilityFunctions.getImageURL() + 'eyeOff.png' :
                      UtilityFunctions.getImageURL() + 'eyeOn.png'}
                    alt="Toggle password"
                  />
                </div>
                {btnClick && !isPasswordValid && <div className="hr-form-error">Password must meet the requirements below</div>}
              </div>

              <div className="hr-form-group">
                <input
                  type="text"
                  placeholder="Demo Code (Optional)"
                  className="hr-form-input"
                  value={affiliateCode}
                  onChange={(e) => setAffiliateCode(e.target.value)}
                  disabled={isLoading || !!localStorage.getItem("referralCode")}
                />
              </div>

              <ul className="hr-validation-list">
                <li className={`hr-validation-item ${password.length <= 0 ? '' : password.length >= 8 && !UtilityFunctions.checkSpaceInPassword(password) ? 'valid' : 'invalid'}`}>
                  ✓ {STRING_CONSTANTS.validationString.min_chars}
                </li>
                <li className={`hr-validation-item ${password.length <= 0 ? '' : hasNumber.test(password) ? 'valid' : 'invalid'}`}>
                  ✓ {STRING_CONSTANTS.validationString.number_req}
                </li>
              </ul>

              {apiError && <div className="hr-api-error">{apiError}</div>}

              <button
                type="submit"
                className="hr-submit-btn"
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? (
                  <span><span className="hr-spinner"></span>Creating Account...</span>
                ) : "GET STARTED NOW"}
              </button>

              <div className="hr-form-footer">
                <p>
                  By signing up, you agree to our{" "}
                  <Link to={terms}>Terms of Service</Link>{" "}and{" "}
                  <Link to={privacy}>Privacy Policy</Link>
                </p>
                <p style={{marginTop: '16px'}}>
                  Already have an account?{" "}
                  <Link to={STRING_CONSTANTS.navigationString.login}>Sign In</Link>
                </p>
              </div>
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="hr-footer">
          <p>Questions? Contact our support team</p>
          <p><a href="mailto:info@mploi.com">info@mploi.com</a></p>
          <p style={{marginTop: '16px'}}>
            <Link to={privacy}>Privacy Policy</Link>{" | "}
            <Link to={terms}>Terms & Conditions</Link>
          </p>
          <p style={{marginTop: '24px', fontSize: '12px'}}>
            All rights reserved. Copyright © {new Date().getFullYear()} MPLOI
          </p>
        </footer>
      </div>
    </Fragment>
  );
};

export default withRouter(HRSimulatorSignup);
